import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../common/types';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { MeiliTrackData } from '../../music24/components/SearchField/types';
import { CreationOmniField } from './CreationOmniField';
import { CreationCatalogRow } from './CreationCatalogRow';
import { CreationTrackRow } from './CreationTrackRow';
import { CreationSearch } from './useCreationSearch';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sheet; each part uses its own slice of it */
import styles from './SMLCreationSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** Four rows. Any more and it is a catalog, and the catalog has its own screen. */
const RECENT_LIMIT = 4;

interface CreationStartStageProps {
  query: string;
  onQueryChange: (query: string) => void;
  onSubmitQuery: () => void;
  search: CreationSearch;
  catalogItems: CatalogItem[];
  catalogTotal: number;
  now: number;
  onPickCatalogItem: (item: CatalogItem) => void;
  onPickTrack: (track: MeiliTrackData) => void;
  onStartBlank: () => void;
}

/**
 * Where creation begins: one field, the artist's own releases under it, and a
 * way out for a page that is not about a release at all.
 *
 * There is no objective to pick. Everything the old first modal asked for is
 * read off whatever goes into the field.
 */
const CreationStartStageComponent: React.FC<CreationStartStageProps> = ({
  query,
  onQueryChange,
  onSubmitQuery,
  search,
  catalogItems,
  catalogTotal,
  now,
  onPickCatalogItem,
  onPickTrack,
  onStartBlank,
}) => {
  const intl = useIntl();
  const recent = useMemo(() => catalogItems.slice(0, RECENT_LIMIT), [catalogItems]);
  const isSearching = query.trim().length >= 2;

  return (
    <>
      <CreationOmniField value={query} onChange={onQueryChange} onSubmit={onSubmitQuery} />

      {isSearching ? (
        <div>
          <div className={styles.hint}>
            <span className={styles.hintLabel}>
              {intl.formatMessage({ id: 'app.sml.create.results.label', defaultMessage: 'In our catalog' })}
            </span>
          </div>
          {search.results === undefined || search.results.length === 0 ? (
            <p className={styles.empty}>
              {search.isSearching
                ? intl.formatMessage({ id: 'app.sml.create.results.searching', defaultMessage: 'Searching…' })
                : intl.formatMessage({
                    id: 'app.sml.create.results.none',
                    defaultMessage: 'Nothing matches. Press Enter to fill it in by hand.',
                  })}
            </p>
          ) : (
            <div className={styles.rows}>
              {search.results.map((track) => (
                <CreationTrackRow key={track.id} track={track} onPick={onPickTrack} />
              ))}
            </div>
          )}
        </div>
      ) : (
        recent.length > 0 && (
          <div>
            <div className={styles.hint}>
              <span className={styles.hintLabel}>
                {intl.formatMessage({
                  id: 'app.sml.create.recent.label',
                  defaultMessage: 'Recent in your catalog',
                })}
              </span>
              <span className={styles.hintValue}>
                {intl.formatMessage(
                  { id: 'app.sml.create.recent.count', defaultMessage: '{shown} of {total}' },
                  { shown: recent.length, total: catalogTotal }
                )}
              </span>
            </div>
            <div className={styles.rows}>
              {recent.map((item) => (
                <CreationCatalogRow key={item.uuid} item={item} now={now} onPick={onPickCatalogItem} />
              ))}
            </div>
          </div>
        )
      )}

      <button type="button" className={styles.blank} onClick={onStartBlank}>
        <span className={styles.blankIcon}>
          <FontAwesomeIcon icon={faPlus} />
        </span>
        <span>
          <span className={styles.blankTitle}>
            {intl.formatMessage({ id: 'app.sml.create.blank.title', defaultMessage: 'Start a blank page' })}
          </span>
          <span className={styles.blankSub}>
            {intl.formatMessage({
              id: 'app.sml.create.blank.subtitle',
              defaultMessage: 'A link hub, a tour page, a merch drop — anything that is not a release.',
            })}
          </span>
        </span>
      </button>
    </>
  );
};

export const CreationStartStage = TypedMemo(CreationStartStageComponent);
